import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '@/components/ui/dialog';
import { Plus, Wallet } from 'lucide-react';
import { DebouncedInput, DebouncedTextarea } from '../shared/DebouncedInputs';

/**
 * Tab "Cajas de Fondo por Rendir": creación y listado.
 */
const CajasFondoTab = ({
  cajasFondo,
  showNewCaja, setShowNewCaja,
  newCaja, setNewCaja,
  handleCreateCaja, handleRendirCaja,
}) => (
  <div className='space-y-4'>
    <div className='flex justify-between items-center mb-4'>
      <h3 className='text-xl font-semibold text-slate-900'>
        Cajas de Fondo por Rendir
      </h3>
      <Dialog open={showNewCaja} onOpenChange={setShowNewCaja}>
        <DialogTrigger asChild>
          <Button className='bg-primary hover:bg-primary/90' data-testid='new-caja-btn'>
            <Plus className='w-4 h-4 mr-2' />
            Nueva Caja
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva Caja de Fondo</DialogTitle>
          </DialogHeader>
          <div className='space-y-4 mt-4'>
            <div>
              <Label>Responsable</Label>
              <DebouncedInput
                value={newCaja.responsable}
                onChange={(e) => setNewCaja({ ...newCaja, responsable: e.target.value })}
                placeholder='Nombre del responsable'
                data-testid='caja-responsable-input'
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <DebouncedTextarea
                value={newCaja.descripcion}
                onChange={(e) => setNewCaja({ ...newCaja, descripcion: e.target.value })}
                rows={3}
                placeholder='Propósito de la caja de fondo'
                data-testid='caja-descripcion-input'
              />
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <Label>Monto (CLP)</Label>
                <DebouncedInput
                  type='number'
                  value={newCaja.monto}
                  onChange={(e) => setNewCaja({ ...newCaja, monto: e.target.value })}
                  data-testid='caja-monto-input'
                />
              </div>
              <div>
                <Label>Fecha Entrega</Label>
                <Input
                  type='date'
                  value={newCaja.fecha_entrega}
                  onChange={(e) => setNewCaja({ ...newCaja, fecha_entrega: e.target.value })}
                  data-testid='caja-fecha-input'
                />
              </div>
            </div>
            <Button onClick={handleCreateCaja} className='w-full' data-testid='create-caja-btn'>
              Crear Caja de Fondo
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>

    {cajasFondo.length === 0 ? (
      <Card className='p-12 text-center border-slate-200'>
        <Wallet className='w-16 h-16 text-slate-400 mx-auto mb-4' />
        <p className='text-slate-600'>No hay cajas de fondo</p>
      </Card>
    ) : (
      <div className='space-y-3'>
        {cajasFondo.map((caja) => (
          <Card key={caja.id} className='p-4 border-slate-200' data-testid={`caja-${caja.id}`}>
            <div className='flex items-center justify-between'>
              <div className='flex-1'>
                <div className='flex items-center gap-3 mb-1'>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    caja.estado === 'Pendiente' ? 'bg-orange-100 text-orange-700' :
                    caja.estado === 'Rendida' ? 'bg-blue-100 text-blue-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {caja.estado}
                  </span>
                  <span className='text-sm font-semibold text-slate-900'>{caja.responsable}</span>
                </div>
                <p className='text-sm text-slate-600 mb-2'>{caja.descripcion}</p>
                <p className='text-xs text-slate-500'>
                  Entregado: {new Date(caja.fecha_entrega).toLocaleDateString('es-CL')}
                </p>
              </div>
              <div className='text-right flex flex-col gap-2'>
                <p className='text-lg font-bold text-slate-900'>
                  ${new Intl.NumberFormat('es-CL').format(caja.monto)}
                </p>
                {caja.estado === 'Pendiente' && (
                  <Button
                    onClick={() => handleRendirCaja(caja.id)}
                    size='sm'
                    variant='outline'
                    data-testid={`rendir-caja-${caja.id}`}
                  >
                    Marcar Rendida
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    )}
  </div>
);

export default CajasFondoTab;
